"use client"

import { createContext, useContext } from "react"

import { useTranslations } from "next-intl"

import { CHECKOUT_PROGRESS } from "@/components/flow/progress"
import { BagIcon, PersonIcon, ShieldCheckIcon } from "@/features/checkout/redesign/Icons"
import { OnboardingHeader } from "@/features/checkout/redesign/OnboardingHeader"
import type { OnboardingPhase } from "@/features/checkout/redesign/phases"

interface OnboardingOptions {
  showInfo: boolean
  showProgress: boolean
  verifyAfterOrder: boolean
}

type PhaseId = "personal" | "identity" | "order"

// The orchestrator sets these from who the shopper is; the screens pass them to OnboardingHeader.
export const OnboardingOptionsContext = createContext<OnboardingOptions>({ showInfo: true, showProgress: true, verifyAfterOrder: false })

export const useOnboardingPhases = (): { phases: readonly OnboardingPhase[]; title: string; phaseIndex: (id: PhaseId) => number } & OnboardingOptions => {
  const t = useTranslations("checkout.onboardingPhases")
  const options = useContext(OnboardingOptionsContext)
  const order: readonly PhaseId[] = options.verifyAfterOrder ? ["personal", "order", "identity"] : ["personal", "identity", "order"]
  const icons = { personal: <PersonIcon width={14} height={16} />, identity: <ShieldCheckIcon />, order: <BagIcon /> }

  return {
    ...options,
    title: t("title"),
    phases: order.map((id) => ({ id, title: t(`${id}.title`), label: t(`${id}.label`), description: t(`${id}.description`), icon: icons[id] })),
    phaseIndex: (id) => order.indexOf(id),
  }
}

type OnboardingPhaseHeaderProps = { onBack?: () => void } & (
  | { at: PhaseId | "overview" | "none" | "done" }
  | { at: "signup"; progressPercentage: number; returningShopper?: boolean }
)

const { SIGNUP_START, SIGNUP_END } = CHECKOUT_PROGRESS

export const OnboardingPhaseHeader = ({ onBack, ...position }: OnboardingPhaseHeaderProps) => {
  const { phases, title, phaseIndex, showInfo, showProgress } = useOnboardingPhases()

  if (position.at === "none") return <OnboardingHeader phases={phases} current={0} phasesTitle={title} onBack={onBack} showInfo={false} showProgress={false} />
  if (position.at === "signup") {
    const guided = !position.returningShopper
    const fraction = (position.progressPercentage - SIGNUP_START) / (SIGNUP_END - SIGNUP_START)
    return <OnboardingHeader phases={phases} current={0} fraction={fraction} phasesTitle={title} onBack={onBack} showInfo={guided} showProgress={guided} />
  }
  const { at } = position
  return (
    <OnboardingHeader
      phases={phases}
      current={at === "overview" ? 0 : at === "done" ? phases.length - 1 : phaseIndex(at)}
      overview={at === "overview"}
      phasesTitle={title}
      onBack={onBack}
      showInfo={showInfo}
      showProgress={showProgress}
    />
  )
}
